import { logger } from '@hatchd/utils';
import {
  BackupAndShelter as BackupAndShelterData,
  GeospatialApi,
  Household as HouseholdMembers,
  Location as LocationData,
  PlanDataTypeEnum,
  PlanDataWhatToTakeAndWhereToStore,
  PlanDataWhenToLeaveWithSelections,
  PlanDataWhereToGoOptions,
  PlanDataWhoToCallOptions,
  Regions,
} from 'api/client';
import Layout from 'components/layout';
import BackupAndShelter from 'components/questions/backup-and-shelter';
import Household from 'components/questions/household';
import Location from 'components/questions/location';
import {
  PlanTitle,
  QuestionContainer,
} from 'components/questions/question.styles';
import WhatToTake from 'components/questions/what-to-take';
import WhenToLeave from 'components/questions/when-to-leave';
import WhereToGo from 'components/questions/where-to-go';
import WhoToCall from 'components/questions/who-to-call';
import SEO from 'components/seo';
import StepNav from 'components/step-nav';
import { useAnalytics } from 'hooks/use-analytics';
import useAuth from 'hooks/use-auth';
import { usePlanAPI } from 'hooks/use-plan';
import { GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useDeepCompareEffect, useMount } from 'react-use';
import { useStore } from 'store';
import { NavigationDirectionEnum, QuestionModeEnum } from 'types/base';
import { backgroundImages } from 'utils/config';
import {
  getPlanStepsForNav,
  getStepFromQuery,
  scrollToTop,
} from 'utils/helpers';
import APP_ROUTES from 'utils/routes';

const PAGE_TITLE = 'Plan to Be Safe and Leave Early';
const PAGE_DESCRIPTION = `Bushfires can quickly turn into a life threatening situation. Don't put yourself and your family at risk. Create your bushfire plan in 15 min or less.`;

interface Props {
  regions: Regions;
}

const Plan: NextPage<Props> = (props) => {
  const router = useRouter();
  const { step } = router.query;
  const { loading, isAuthenticated, isRegisteredUser, startAnonymousSession } =
    useAuth();
  const [getPlanId] = useStore((state) => [state.getPlanId]);
  const getPlanDraftDirtyFlag = useStore(
    (state) => state.getPlanDraftDirtyFlag
  );
  const getPlanDraft = useStore((state) => state.getPlanDraft);
  const [set, draftPlan, planProgress] = useStore((state) => [
    state.set,
    state.plan.draft,
    state.progress,
  ]);
  const [currentStep, setCurrentStep] = useState<number>(
    getStepFromQuery(step)
  );

  const { usePlans, useUpsertPlan } = usePlanAPI();
  const updateRemotePlan = useUpsertPlan();
  const { isFetched } = usePlans();
  const { logPlanStepView } = useAnalytics();

  // Generate the steps for the step navigation
  const steps = getPlanStepsForNav(draftPlan, 7);
  const showControls = currentStep < 8;

  // CALLBACKS -------------------------------------------------------------------------------------

  const handleNavigate = (direction: NavigationDirectionEnum) => {
    if (direction === NavigationDirectionEnum.Previous) {
      setCurrentStep((currentStep) =>
        currentStep === 1 ? 1 : currentStep - 1
      );
    } else {
      setCurrentStep((currentStep) => (currentStep < 9 ? currentStep + 1 : 1));
    }
    // Take the user to the top of the page for each question in the plan...
    scrollToTop();
  };

  const handleSetLocation = useCallback(
    (location: LocationData) => {
      set(({ plan }) => {
        plan.draft.location = location;
        plan.isDirty = true;
      }, 'setLocation');
    },
    [set]
  );

  const handleSetHousehold = useCallback(
    (household: HouseholdMembers) => {
      set(({ plan }) => {
        plan.draft.household = household;
        plan.isDirty = true;
      }, 'setHousehold');
    },
    [set]
  );

  const handleSetWhenToLeave = useCallback(
    (whenToLeaveWithSelections: PlanDataWhenToLeaveWithSelections) => {
      set(({ plan }) => {
        plan.draft.whenToLeaveWithSelections = whenToLeaveWithSelections;
        plan.isDirty = true;
      }, 'setWhenToLeave');
    },
    [set]
  );

  const handleSetWhereToGo = useCallback(
    (whereToGoOptions: PlanDataWhereToGoOptions) => {
      set(({ plan }) => {
        plan.draft.whereToGoOptions = whereToGoOptions;
        plan.isDirty = true;
      }, 'setWhereToGo');
    },
    [set]
  );

  const handleSetWhoToCall = useCallback(
    (whoToCall: PlanDataWhoToCallOptions) => {
      set(({ plan }) => {
        plan.draft.whoToCallOptions = whoToCall;
        plan.isDirty = true;
      }, 'setWhoToCall');
    },
    [set]
  );

  const handleSetWhatToTake = useCallback(
    (whatToTake: PlanDataWhatToTakeAndWhereToStore) => {
      set(({ plan }) => {
        plan.draft.whatToTakeAndWhereToStore = whatToTake;
        plan.isDirty = true;
      }, 'setWhatToTake');
    },
    [set]
  );

  const handleSetBackupAndShelter = useCallback(
    (backupAndShelter: BackupAndShelterData) => {
      set(({ plan }) => {
        plan.draft.backupAndShelter = backupAndShelter;
        plan.isDirty = true;
      }, 'setBackupAndShelter');
    },
    [set]
  );

  const savePlan = useCallback(async () => {
    const currentDraft = getPlanDraft();
    const currentPlanId = getPlanId();
    const isPlanDirty = getPlanDraftDirtyFlag();

    if (!isPlanDirty) {
      logger.log('LE Plan: Clean, nothing to save');
      return;
    }

    logger.info('LE Plan: Dirty, saving!');

    try {
      await updateRemotePlan.mutateAsync({
        id: currentPlanId,
        plan: currentDraft,
      });
    } catch (error) {
      logger.error('Error saving plan', error);
    }
  }, [getPlanDraft, getPlanDraftDirtyFlag, getPlanId, updateRemotePlan]);

  // EFFECTS ---------------------------------------------------------------------------------------

  useEffect(() => {
    // Start an anonymous session unless we already have a current session.
    if (!loading && !isAuthenticated) {
      startAnonymousSession();
    }
  }, [loading, startAnonymousSession, isAuthenticated]);

  useEffect(() => {
    if (currentStep > 7) {
      router.push(
        `${APP_ROUTES.leave.summary}/?navigatedFromPlan=true`,
        APP_ROUTES.leave.summary
      );
    }
  }, [currentStep, router]);

  useEffect(() => {
    if (planProgress.planStep !== currentStep) {
      const planStep = currentStep < 8 ? currentStep : 1;

      // Ignore step 8 (the checklist step)
      if (currentStep < 8) {
        logPlanStepView(planStep);
      }

      set(({ progress, plan }) => {
        progress.planStep = planStep;
        plan.draft.type = PlanDataTypeEnum.Leave;
      }, 'setCurrentPlanStep');
    }
  }, [currentStep, logPlanStepView, planProgress.planStep, set]);

  useMount(() => {
    // Specifically log the first step when the user starts/edits the plan
    if (currentStep === 1) {
      logPlanStepView(currentStep);
    }
  });

  useDeepCompareEffect(() => {
    // REGISTERED USERS ONLY:
    // If they have a current plan, enable auto-save.
    if (isRegisteredUser && isFetched) {
      const currentPlanId = getPlanId();
      if (currentPlanId) {
        savePlan();
      }
    }
  }, [draftPlan, isRegisteredUser, isFetched]);

  return (
    <Layout
      mainBackground={backgroundImages.treesBurn}
      showPlanControls={showControls}
    >
      <SEO title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
      <QuestionContainer>
        <PlanTitle as='h1'>{draftPlan.name || 'My leave early plan'}</PlanTitle>
        {showControls && (
          <StepNav
            steps={steps}
            currentStep={currentStep}
            onClick={setCurrentStep}
          />
        )}
        {currentStep === 1 && (
          <Location
            location={draftPlan.location}
            regions={props.regions}
            mode={QuestionModeEnum.Plan}
            onSetLocation={handleSetLocation}
            navigate={handleNavigate}
          />
        )}
        {currentStep === 2 && (
          <Household
            household={draftPlan.household}
            mode={QuestionModeEnum.Plan}
            onSetHousehold={handleSetHousehold}
            navigate={handleNavigate}
          />
        )}
        {currentStep === 3 && (
          <WhenToLeave
            whenToLeave={draftPlan.whenToLeaveWithSelections}
            onSetWhenToLeave={handleSetWhenToLeave}
            navigate={handleNavigate}
          />
        )}
        {currentStep === 4 && (
          <WhereToGo
            whereToGoOptions={draftPlan.whereToGoOptions}
            onSetWhereToGo={handleSetWhereToGo}
            navigate={handleNavigate}
          />
        )}
        {currentStep === 5 && (
          <WhoToCall
            whoToCall={draftPlan.whoToCallOptions}
            onSetWhoToCall={handleSetWhoToCall}
            navigate={handleNavigate}
          />
        )}
        {currentStep === 6 && (
          <WhatToTake
            whatToTake={draftPlan.whatToTakeAndWhereToStore}
            onSetWhatToTake={handleSetWhatToTake}
            navigate={handleNavigate}
          />
        )}
        {currentStep === 7 && (
          <BackupAndShelter
            backupAndShelter={draftPlan.backupAndShelter}
            onSetBackupAndShelter={handleSetBackupAndShelter}
            navigate={handleNavigate}
          />
        )}
      </QuestionContainer>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const GeoApi = new GeospatialApi();
  const { data: regions } = await GeoApi.getRegions();

  return {
    props: {
      regions,
    },
  };
};

export default Plan;
